import { Shield, Lock, Server, Eye, Users, FileCheck, AlertTriangle, Mail } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Container } from "@/components/layout/Container";
import { Section } from "@/components/layout/Section";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AnimatedSection } from "@/components/ui/animated-section";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const securityFeatures = [
  {
    icon: Lock,
    title: "Encryption at Rest & In Transit",
    description: "All data is encrypted using AES-256 encryption at rest and TLS 1.3 for data in transit.",
  },
  {
    icon: Server,
    title: "Secure Infrastructure",
    description: "Hosted on enterprise-grade cloud infrastructure with 99.9% uptime SLA and redundancy.",
  },
  {
    icon: Eye,
    title: "Access Controls",
    description: "Role-based access control (RBAC) ensures users only access data they're authorized to see.",
  },
  {
    icon: Users,
    title: "Authentication Security",
    description: "Secure authentication with OAuth 2.0, session management, and optional multi-factor authentication.",
  },
  {
    icon: FileCheck,
    title: "Regular Audits",
    description: "We conduct regular security audits and penetration testing to identify and address vulnerabilities.",
  },
  {
    icon: AlertTriangle,
    title: "Incident Response",
    description: "24/7 monitoring with a dedicated incident response plan to quickly address any security issues.",
  },
];

export default function Security() {
  useDocumentMeta({
    title: 'Security',
    description: "Learn about SurviAI's security measures, data protection, and compliance standards."
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <Section className="pt-24 pb-16">
        <Container>
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="secondary" className="mb-4">
              <Shield className="h-3 w-3 mr-1" />
              Security First
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Your Data Security is Our Priority
            </h1>
            <p className="text-xl text-muted-foreground">
              We implement industry-leading security measures to protect your surveys, 
              responses, and personal information.
            </p>
          </AnimatedSection>

          {/* Security Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {securityFeatures.map((feature) => (
              <Card key={feature.title} className="hover-lift">
                <CardContent className="pt-6">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Security Information */}
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Data Protection</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We take a defense-in-depth approach to data protection, implementing multiple layers 
                  of security controls to protect your information:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>All data encrypted at rest using AES-256 encryption</li>
                  <li>TLS 1.3 encryption for all data in transit</li>
                  <li>Database encryption with regularly rotated keys</li>
                  <li>Encrypted backups stored in geographically separate locations</li>
                  <li>Secure key management using hardware security modules (HSM)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Infrastructure Security</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Our infrastructure is designed with security as a foundational requirement:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Hosted on SOC 2 Type II certified cloud providers</li>
                  <li>Network segmentation and firewall protection</li>
                  <li>DDoS protection and mitigation</li>
                  <li>Intrusion detection and prevention systems</li>
                  <li>Regular vulnerability scanning and patching</li>
                  <li>Automated security updates and monitoring</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Application Security</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We follow secure development practices throughout our software lifecycle:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Secure coding practices following OWASP guidelines</li>
                  <li>Regular code reviews and security audits</li>
                  <li>Automated security testing in CI/CD pipeline</li>
                  <li>Input validation and output encoding</li>
                  <li>Protection against common vulnerabilities (XSS, CSRF, SQL injection)</li>
                  <li>Content Security Policy (CSP) headers</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Access Control</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We implement strict access controls to protect your data:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Role-based access control (RBAC) for all users</li>
                  <li>Principle of least privilege for employee access</li>
                  <li>Multi-factor authentication available for all accounts</li>
                  <li>Session timeout and automatic logout</li>
                  <li>Audit logging of all access and changes</li>
                  <li>Regular access reviews and revocation</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Compliance</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We are committed to meeting industry standards and regulatory requirements:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>GDPR compliant data processing</li>
                  <li>CCPA compliance for California residents</li>
                  <li>SOC 2 Type II certification (in progress)</li>
                  <li>Regular third-party security assessments</li>
                  <li>Data processing agreements available upon request</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Incident Response</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We maintain a comprehensive incident response program:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>24/7 security monitoring and alerting</li>
                  <li>Documented incident response procedures</li>
                  <li>Regular incident response drills</li>
                  <li>Notification within 72 hours of confirmed breach</li>
                  <li>Post-incident analysis and improvement</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Responsible Disclosure</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We welcome security researchers to responsibly disclose any vulnerabilities they discover. 
                  If you believe you've found a security issue, please report it to us:
                </p>
                <Card className="bg-muted/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <a href="mailto:security@survi.ai" className="text-primary hover:underline font-medium">
                        security@survi.ai
                      </a>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Please include detailed information about the vulnerability and steps to reproduce.
                    </p>
                  </CardContent>
                </Card>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Questions?</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about our security practices or would like to request 
                  additional security documentation, please contact us at{" "}
                  <a href="mailto:security@survi.ai" className="text-primary hover:underline">
                    security@survi.ai
                  </a>
                </p>
              </section>
            </div>
          </div>
        </Container>
      </Section>

      <Footer />
    </div>
  );
}
